import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    workOrders: 'Work Orders',
    approvals: 'Approvals',
    invoices: 'Invoices',
    users: 'Users',
    logout: 'Log out',
    
    // Common
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    cancel: 'Cancel',
    save: 'Save',
    submit: 'Submit',
    back: 'Back',
    search: 'Search',
    filter: 'Filter',
    loading: 'Loading',
    yes: 'Yes',
    no: 'No',
    confirm: 'Confirm',
    close: 'Close',
    update: 'Update',
    
    // Auth
    welcomeBack: 'Welcome Back',
    enterCredentials: 'Enter your credentials to access your account',
    username: 'Username',
    password: 'Password',
    login: 'Login',
    loggingIn: 'Logging in...',
    
    // Dashboard
    totalWorkOrders: 'Total Work Orders',
    inProgress: 'In Progress',
    pendingApproval: 'Pending Approval',
    completed: 'Completed',
    totalCost: 'Total Project Cost',
    recentActivity: 'Recent Activity',
    overviewText: 'Overview of your work orders and projects',
    
    // Work Orders
    newWorkOrder: 'New Work Order',
    workOrderDetails: 'Work Order Details',
    title: 'Title',
    description: 'Description',
    workType: 'Work Type',
    priority: 'Priority',
    status: 'Status',
    sectionCode: 'Section Code',
    contractor: 'Contractor',
    deadline: 'Deadline',
    estimatedCost: 'Estimated Cost',
    actualCost: 'Actual Cost',
    farm: 'Farm',
    workOrderNumber: 'Work Order Number',
    estimatedDays: 'Estimated Days',
    costType: 'Cost Type',
    quantity: 'Quantity',
    unitCost: 'Unit Cost',
    currency: 'Currency',
    materialsFarmProvides: 'Farm Materials',
    materialsContractorBrings: 'Contractor Materials',
    paymentTerms: 'Payment Terms',
    paymentTermsComment: 'Payment Terms Comment',
    createWorkOrder: 'Create Work Order',
    updateWorkOrder: 'Update Work Order',
    manageWorkOrders: 'Manage and track all work orders',
    searchWorkOrders: 'Search work orders...',
    filterByStatus: 'Filter by status',
    filterByPriority: 'Filter by priority',
    allStatuses: 'All Statuses',
    allPriorities: 'All Priorities',
    noWorkOrdersFound: 'No work orders found',
    tryAdjustingFilters: 'Try adjusting your filters',
    createFirstWorkOrder: 'Create your first work order',
    workOrderCreated: 'Work order created successfully',
    workOrderUpdated: 'Work order updated successfully',
    workOrderDeleted: 'Work order deleted successfully',
    confirmDelete: 'Are you sure you want to delete this work order?',
    
    // Work Order Form
    enterWorkOrderTitle: 'Enter work order title',
    detailedDescription: 'Detailed description of the work',
    selectContractor: 'Select a contractor',
    unassigned: 'Unassigned',
    selectPaymentTerms: 'Select payment terms',
    notSpecified: 'Not specified',
    days30: '30 days',
    days60: '60 days',
    days90: '90 days',
    afterWorkCompletion: 'after work completion',
    additionalPaymentTerms: 'Any additional payment terms or conditions...',
    listMaterialsFarm: 'List materials the farm will provide...',
    listMaterialsContractor: 'List materials the contractor needs to bring...',
    autoCalculated: 'Auto-calculated',
    
    // Work Types
    pruning: 'Pruning',
    harvesting: 'Harvesting',
    irrigation: 'Irrigation',
    pestControl: 'Pest Control',
    fertilization: 'Fertilization',
    equipmentMaintenance: 'Equipment Maintenance',
    architectural: 'Architectural',
    task: 'Task',
    other: 'Other',
    
    // Task Type
    internalStaff: 'Internal Staff',
    assignedStaff: 'Assigned Staff',
    selectStaff: 'Select a staff member',
    noStaffMembers: 'No staff members found. Add staff in Staff Management.',
    taskTypeNote: 'Task type is for internal staff with no labor cost.',
    taskNoCostNote: 'Tasks for internal staff have no labor costs. You can add material costs if needed.',
    materialCostOnly: 'Material Cost (Optional)',
    materialCost: 'Material Cost',
    taskConfirmation: 'Task Confirmation',
    confirmTask: 'Confirm Task',
    taskConfirmed: 'Task Confirmed',
    pendingTasks: 'Pending Tasks',
    tasksNeedingConfirmation: 'Tasks that need manager confirmation',
    noTasksPending: 'No tasks pending confirmation',
    
    // Staff Management
    staffManagement: 'Staff Management',
    manageStaff: 'Manage internal farm staff members',
    createStaff: 'Add Staff',
    createNewStaff: 'Add New Staff Member',
    addNewStaff: 'Add a new internal staff member to the system',
    staffName: 'Name',
    staffPosition: 'Position',
    staffDepartment: 'Department',
    staffPhone: 'Phone',
    staffEmail: 'Email',
    totalStaff: 'Total Staff',
    activeStaff: 'Active',
    staffCreated: 'Staff member created successfully',
    staffUpdated: 'Staff member updated successfully',
    staffDeleted: 'Staff member deactivated successfully',
    
    // Staff Performance Dashboard
    staffPerformance: 'Staff Performance',
    staffPerformanceDesc: 'Track task completion rates and workload distribution',
    totalTasks: 'Total Tasks',
    completedTasks: 'Completed',
    pendingTasks: 'Pending',
    completionRate: 'Completion Rate',
    individualPerformance: 'Individual Performance',
    taskCompletion: 'Task Completion',
    avgDays: 'Avg. Days',
    recentTasks: 'Recent Tasks',
    confirmed: 'Confirmed',
    pending: 'Pending',
    noStaffData: 'No performance data',
    noStaffDataDesc: 'Add staff members and assign tasks to see performance metrics',
    
    // Attachments
    attachments: 'Attachments',
    uploadFile: 'Upload File',
    uploadFileDesc: 'Upload documents, images, or videos to this work order',
    noAttachments: 'No attachments yet',
    supportedFormats: 'Supported: PDF, Word, Excel, AutoCAD, Images, Videos',
    descriptionOptional: 'Description (Optional)',
    fileDescriptionPlaceholder: 'Add a description for this file...',
    uploading: 'Uploading...',
    upload: 'Upload',
    preview: 'Preview',
    download: 'Download',
    fileUploaded: 'File uploaded successfully',
    uploadFailed: 'Failed to upload file',
    fileTooLarge: 'File is too large. Maximum size is 50MB',
    confirmDeleteAttachment: 'Are you sure you want to delete this attachment?',
    attachmentDeleted: 'Attachment deleted successfully',
    deleteFailed: 'Failed to delete attachment',
    
    // User Roles
    architect: 'Architect',
    
    // Priority
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
    
    // Status
    draft: 'Draft',
    pending: 'Pending',
    rejected: 'Rejected',
    awaitingInvoice: 'Awaiting Invoice',
    invoiced: 'Invoiced',
    
    // Cost Types
    perTree: 'Per Tree',
    perHectare: 'Per Hectare',
    perTask: 'Per Task',
    trees: 'Trees',
    hectares: 'Hectares',
    
    // Farms
    laPunta: 'La Punta',
    rancagua: 'Rancagua',
    
    // Sorting
    sortByDate: 'Sort by Date',
    sortByContractor: 'Sort by Contractor',
    sortByType: 'Sort by Type',
    sortBySection: 'Sort by Section',
    sortByFarm: 'Sort by Farm',
    sortByAmount: 'Sort by Amount',
    
    // Work Order Detail
    photos: 'Photos',
    uploadPhoto: 'Upload Photo',
    uploading: 'Uploading...',
    noPhotos: 'No photos uploaded yet',
    comments: 'Comments',
    addComment: 'Add a comment...',
    updateStatus: 'Update Status',
    timeline: 'Timeline',
    created: 'Created',
    approved: 'Approved',
    by: 'by',
    type: 'Type',
    section: 'Section',
    
    // Approvals
    approvalQueue: 'Approval Queue',
    reviewApprovals: 'Review and approve work orders pending approval',
    review: 'Review',
    allCaughtUp: 'All caught up!',
    noPendingApprovals: 'No work orders pending approval',
    reviewWorkOrder: 'Review Work Order',
    rejectionReason: 'Rejection Reason',
    rejectionReasonOptional: 'Rejection Reason (Optional - required if rejecting)',
    provideRejectionReason: 'Provide a reason if rejecting this work order...',
    approve: 'Approve',
    reject: 'Reject',
    approving: 'Approving...',
    rejecting: 'Rejecting...',
    workOrderApproved: 'Work order approved successfully',
    workOrderRejected: 'Work order rejected',
    
    // Invoices
    trackInvoices: 'Track and manage all invoices',
    totalAmount: 'Total Amount',
    pendingAmount: 'Pending',
    paidAmount: 'Paid',
    searchInvoices: 'Search by invoice number or contractor...',
    noInvoicesFound: 'No invoices found',
    invoicesAppear: 'Invoices will appear here once work orders are approved',
    invoiceNumber: 'Invoice Number',
    amount: 'Amount',
    markAsPaid: 'Mark as Paid',
    processing: 'Processing...',
    invoiceMarkedPaid: 'Invoice marked as paid',
    
    // Users
    userManagement: 'User Management',
    manageUsers: 'Manage users and their access levels',
    createUser: 'Create User',
    createNewUser: 'Create New User',
    addNewUser: 'Add a new user to the system with their role and credentials',
    fullName: 'Full Name',
    email: 'Email',
    role: 'Role',
    totalUsers: 'Total Users',
    admins: 'Admins',
    managers: 'Managers',
    contractors: 'Contractors',
    allUsers: 'All Users',
    userCreated: 'User created successfully',
    accessDenied: 'Access Denied',
    noPermission: "You don't have permission to access this page.",
    
    // Roles
    admin: 'Admin',
    projectManager: 'Project Manager',
    mainManager: 'Main Manager',
    sectionManager: 'Section Manager',
    
    // Notifications
    notifications: 'Notifications',
    unread: 'unread',
    noNotifications: 'No notifications',
    markAsRead: 'Mark as read',
    
    // Messages
    failedToLoad: 'Failed to load',
    successfullySaved: 'Successfully saved',
    errorOccurred: 'An error occurred',
    noRecentActivity: 'No recent activity',
    costInformation: 'Cost Information',
    materials: 'Materials',
    saving: 'Saving...',
    creatingUser: 'Creating...',
    
    // Other
    optional: 'Optional',
    required: 'Required',
  },
  es: {
    // Navigation
    dashboard: 'Panel de Control',
    workOrders: 'Órdenes de Trabajo',
    approvals: 'Aprobaciones',
    invoices: 'Facturas',
    users: 'Usuarios',
    logout: 'Cerrar Sesión',
    
    // Common
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    cancel: 'Cancelar',
    save: 'Guardar',
    submit: 'Enviar',
    back: 'Volver',
    search: 'Buscar',
    filter: 'Filtrar',
    loading: 'Cargando',
    yes: 'Sí',
    no: 'No',
    confirm: 'Confirmar',
    close: 'Cerrar',
    update: 'Actualizar',
    
    // Auth
    welcomeBack: 'Bienvenido de Nuevo',
    enterCredentials: 'Ingresa tus credenciales para acceder a tu cuenta',
    username: 'Usuario',
    password: 'Contraseña',
    login: 'Iniciar Sesión',
    loggingIn: 'Iniciando sesión...',
    
    // Dashboard
    totalWorkOrders: 'Total de Órdenes',
    inProgress: 'En Progreso',
    pendingApproval: 'Pendiente de Aprobación',
    completed: 'Completado',
    totalCost: 'Costo Total del Proyecto',
    recentActivity: 'Actividad Reciente',
    overviewText: 'Vista general de tus órdenes de trabajo y proyectos',
    
    // Work Orders
    newWorkOrder: 'Nueva Orden de Trabajo',
    workOrderDetails: 'Detalles de la Orden',
    title: 'Título',
    description: 'Descripción',
    workType: 'Tipo de Trabajo',
    priority: 'Prioridad',
    status: 'Estado',
    sectionCode: 'Código de Sección',
    contractor: 'Contratista',
    deadline: 'Fecha Límite',
    estimatedCost: 'Costo Estimado',
    actualCost: 'Costo Real',
    farm: 'Finca',
    workOrderNumber: 'Número de Orden',
    estimatedDays: 'Días Estimados',
    costType: 'Tipo de Costo',
    quantity: 'Cantidad',
    unitCost: 'Costo Unitario',
    currency: 'Moneda',
    materialsFarmProvides: 'Materiales de la Finca',
    materialsContractorBrings: 'Materiales del Contratista',
    paymentTerms: 'Términos de Pago',
    paymentTermsComment: 'Comentario de Términos de Pago',
    createWorkOrder: 'Crear Orden de Trabajo',
    updateWorkOrder: 'Actualizar Orden de Trabajo',
    manageWorkOrders: 'Gestionar y rastrear todas las órdenes de trabajo',
    searchWorkOrders: 'Buscar órdenes de trabajo...',
    filterByStatus: 'Filtrar por estado',
    filterByPriority: 'Filtrar por prioridad',
    allStatuses: 'Todos los Estados',
    allPriorities: 'Todas las Prioridades',
    noWorkOrdersFound: 'No se encontraron órdenes de trabajo',
    tryAdjustingFilters: 'Intenta ajustar tus filtros',
    createFirstWorkOrder: 'Crea tu primera orden de trabajo',
    workOrderCreated: 'Orden de trabajo creada exitosamente',
    workOrderUpdated: 'Orden de trabajo actualizada exitosamente',
    workOrderDeleted: 'Orden de trabajo eliminada exitosamente',
    confirmDelete: '¿Estás seguro de que deseas eliminar esta orden de trabajo?',
    
    // Work Order Form
    enterWorkOrderTitle: 'Ingresa el título de la orden de trabajo',
    detailedDescription: 'Descripción detallada del trabajo',
    selectContractor: 'Seleccionar un contratista',
    unassigned: 'Sin Asignar',
    selectPaymentTerms: 'Seleccionar términos de pago',
    notSpecified: 'No especificado',
    days30: '30 días',
    days60: '60 días',
    days90: '90 días',
    afterWorkCompletion: 'después de completar el trabajo',
    additionalPaymentTerms: 'Cualquier término o condición de pago adicional...',
    listMaterialsFarm: 'Lista de materiales que proporcionará la finca...',
    listMaterialsContractor: 'Lista de materiales que debe traer el contratista...',
    autoCalculated: 'Calculado automáticamente',
    
    // Work Types
    pruning: 'Poda',
    harvesting: 'Cosecha',
    irrigation: 'Riego',
    pestControl: 'Control de Plagas',
    fertilization: 'Fertilización',
    equipmentMaintenance: 'Mantenimiento de Equipos',
    architectural: 'Arquitectónico',
    task: 'Tarea',
    other: 'Otro',
    
    // Task Type
    internalStaff: 'Personal Interno',
    assignedStaff: 'Personal Asignado',
    selectStaff: 'Seleccionar un miembro del personal',
    noStaffMembers: 'No se encontraron miembros del personal. Agregue personal en Gestión de Personal.',
    taskTypeNote: 'El tipo Tarea es para personal interno sin costo de mano de obra.',
    taskNoCostNote: 'Las tareas para personal interno no tienen costos de mano de obra. Puede agregar costos de materiales si es necesario.',
    materialCostOnly: 'Costo de Materiales (Opcional)',
    materialCost: 'Costo de Materiales',
    taskConfirmation: 'Confirmación de Tarea',
    confirmTask: 'Confirmar Tarea',
    taskConfirmed: 'Tarea Confirmada',
    pendingTasks: 'Tareas Pendientes',
    tasksNeedingConfirmation: 'Tareas que necesitan confirmación del gerente',
    noTasksPending: 'No hay tareas pendientes de confirmación',
    
    // Staff Management
    staffManagement: 'Gestión de Personal',
    manageStaff: 'Gestionar miembros del personal interno de la finca',
    createStaff: 'Agregar Personal',
    createNewStaff: 'Agregar Nuevo Miembro del Personal',
    addNewStaff: 'Agregar un nuevo miembro del personal interno al sistema',
    staffName: 'Nombre',
    staffPosition: 'Cargo',
    staffDepartment: 'Departamento',
    staffPhone: 'Teléfono',
    staffEmail: 'Correo Electrónico',
    totalStaff: 'Total de Personal',
    activeStaff: 'Activo',
    staffCreated: 'Miembro del personal creado exitosamente',
    staffUpdated: 'Miembro del personal actualizado exitosamente',
    staffDeleted: 'Miembro del personal desactivado exitosamente',
    
    // Staff Performance Dashboard
    staffPerformance: 'Rendimiento del Personal',
    staffPerformanceDesc: 'Seguimiento de tasas de finalización y distribución de carga de trabajo',
    totalTasks: 'Total de Tareas',
    completedTasks: 'Completadas',
    pendingTasks: 'Pendientes',
    completionRate: 'Tasa de Finalización',
    individualPerformance: 'Rendimiento Individual',
    taskCompletion: 'Finalización de Tareas',
    avgDays: 'Días Prom.',
    recentTasks: 'Tareas Recientes',
    confirmed: 'Confirmada',
    pending: 'Pendiente',
    noStaffData: 'Sin datos de rendimiento',
    noStaffDataDesc: 'Agregue personal y asigne tareas para ver métricas de rendimiento',
    
    // Attachments
    attachments: 'Archivos Adjuntos',
    uploadFile: 'Subir Archivo',
    uploadFileDesc: 'Suba documentos, imágenes o videos a esta orden de trabajo',
    noAttachments: 'Sin archivos adjuntos',
    supportedFormats: 'Soportados: PDF, Word, Excel, AutoCAD, Imágenes, Videos',
    descriptionOptional: 'Descripción (Opcional)',
    fileDescriptionPlaceholder: 'Agregue una descripción para este archivo...',
    uploading: 'Subiendo...',
    upload: 'Subir',
    preview: 'Vista Previa',
    download: 'Descargar',
    fileUploaded: 'Archivo subido exitosamente',
    uploadFailed: 'Error al subir archivo',
    fileTooLarge: 'El archivo es demasiado grande. Tamaño máximo: 50MB',
    confirmDeleteAttachment: '¿Está seguro de que desea eliminar este archivo adjunto?',
    attachmentDeleted: 'Archivo adjunto eliminado exitosamente',
    deleteFailed: 'Error al eliminar archivo',
    
    // User Roles
    architect: 'Arquitecto',
    
    // Priority
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
    urgent: 'Urgente',
    
    // Status
    draft: 'Borrador',
    pending: 'Pendiente',
    rejected: 'Rechazado',
    awaitingInvoice: 'Esperando Factura',
    invoiced: 'Facturado',
    
    // Cost Types
    perTree: 'Por Árbol',
    perHectare: 'Por Hectárea',
    perTask: 'Por Tarea',
    trees: 'Árboles',
    hectares: 'Hectáreas',
    
    // Farms
    laPunta: 'La Punta',
    rancagua: 'Rancagua',
    
    // Sorting
    sortByDate: 'Ordenar por Fecha',
    sortByContractor: 'Ordenar por Contratista',
    sortByType: 'Ordenar por Tipo',
    sortBySection: 'Ordenar por Sección',
    sortByFarm: 'Ordenar por Finca',
    sortByAmount: 'Ordenar por Monto',
    
    // Work Order Detail
    photos: 'Fotos',
    uploadPhoto: 'Subir Foto',
    uploading: 'Subiendo...',
    noPhotos: 'No se han subido fotos aún',
    comments: 'Comentarios',
    addComment: 'Agregar un comentario...',
    updateStatus: 'Actualizar Estado',
    timeline: 'Línea de Tiempo',
    created: 'Creado',
    approved: 'Aprobado',
    by: 'por',
    type: 'Tipo',
    section: 'Sección',
    
    // Approvals
    approvalQueue: 'Cola de Aprobaciones',
    reviewApprovals: 'Revisar y aprobar órdenes de trabajo pendientes de aprobación',
    review: 'Revisar',
    allCaughtUp: '¡Todo al día!',
    noPendingApprovals: 'No hay órdenes de trabajo pendientes de aprobación',
    reviewWorkOrder: 'Revisar Orden de Trabajo',
    rejectionReason: 'Razón de Rechazo',
    rejectionReasonOptional: 'Razón de Rechazo (Opcional - requerido si rechaza)',
    provideRejectionReason: 'Proporcione una razón si rechaza esta orden de trabajo...',
    approve: 'Aprobar',
    reject: 'Rechazar',
    approving: 'Aprobando...',
    rejecting: 'Rechazando...',
    workOrderApproved: 'Orden de trabajo aprobada exitosamente',
    workOrderRejected: 'Orden de trabajo rechazada',
    
    // Invoices
    trackInvoices: 'Rastrear y gestionar todas las facturas',
    totalAmount: 'Monto Total',
    pendingAmount: 'Pendiente',
    paidAmount: 'Pagado',
    searchInvoices: 'Buscar por número de factura o contratista...',
    noInvoicesFound: 'No se encontraron facturas',
    invoicesAppear: 'Las facturas aparecerán aquí una vez que se aprueben las órdenes de trabajo',
    invoiceNumber: 'Número de Factura',
    amount: 'Monto',
    markAsPaid: 'Marcar como Pagado',
    processing: 'Procesando...',
    invoiceMarkedPaid: 'Factura marcada como pagada',
    
    // Users
    userManagement: 'Gestión de Usuarios',
    manageUsers: 'Gestionar usuarios y sus niveles de acceso',
    createUser: 'Crear Usuario',
    createNewUser: 'Crear Nuevo Usuario',
    addNewUser: 'Agregar un nuevo usuario al sistema con su rol y credenciales',
    fullName: 'Nombre Completo',
    email: 'Correo Electrónico',
    role: 'Rol',
    totalUsers: 'Total de Usuarios',
    admins: 'Administradores',
    managers: 'Gerentes',
    contractors: 'Contratistas',
    allUsers: 'Todos los Usuarios',
    userCreated: 'Usuario creado exitosamente',
    accessDenied: 'Acceso Denegado',
    noPermission: 'No tienes permiso para acceder a esta página.',
    
    // Roles
    admin: 'Administrador',
    projectManager: 'Gerente de Proyecto',
    mainManager: 'Gerente Principal',
    sectionManager: 'Gerente de Sección',
    
    // Notifications
    notifications: 'Notificaciones',
    unread: 'sin leer',
    noNotifications: 'No hay notificaciones',
    markAsRead: 'Marcar como leído',
    
    // Messages
    failedToLoad: 'Error al cargar',
    successfullySaved: 'Guardado exitosamente',
    errorOccurred: 'Ocurrió un error',
    noRecentActivity: 'No hay actividad reciente',
    costInformation: 'Información de Costos',
    materials: 'Materiales',
    saving: 'Guardando...',
    creatingUser: 'Creando...',
    
    // Other
    optional: 'Opcional',
    required: 'Requerido',
  },
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'es' : 'en'));
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
